import qs from "qs";
import { MessageBox } from "element-ui";
import SocketEventCenter from "./SocketEventCenter";
import { tokenExpired } from "@/router/tokenExpired";
import router from "@/router";

// 全局WebSocket连接管理器
class WebSocketConnectionManager {
  constructor() {
    this.connections = new Map();
  }

  addConnection(key, wsInstance) {
    this.connections.set(key, wsInstance);
  }

  removeConnection(key) {
    const ws = this.connections.get(key);
    // console.log("removeConnection", ws);
    if (ws && ws.socket) {
      ws.socket.close();
    }
    // if (ws) {
    //   ws.close();
    // }
    this.connections.delete(key);
  }

  closeAllConnections() {
    // console.log(this.connections);
    for (const [, ws] of this.connections) {
      // 兜底判空：某个实例的 socket 可能已为空，避免整个关闭流程被中断
      if (ws && ws.socket) {
        ws.socket.close();
      }
    }
    this.connections.clear();
  }

  getConnectionCount() {
    return this.connections.size;
  }
}

export const globalWebSocketManager = new WebSocketConnectionManager();

export default class WebSocketManager {
  socket = null;
  #reconnectAttempts = 0;
  #lastOpenedAt = 0; // 上次成功建立连接的毫秒时间戳，用于识别“连上即断”的异常连接
  #hasOpened = false; // 只有曾建立过连接，再次打开才算断线重连
  #pingIntervalId = null;
  #manualShutDown = false;
  #MAX_RECONNECT_ATTEMPTS = 10;
  #RECONNECT_INTERVAL = 1000;
  #PING_INTERVAL = 30000;

  constructor(url, param, { onMessage, onOpen, onReconnect, emitGlobal = true } = {}) {
    this.url = `${url}?${qs.stringify(param)}`;
    this.funcName = param.func_name;
    this.param = param;
    // 页面可独立监听本连接并关闭全局广播；旧调用默认仍使用事件中心。
    this.onMessage = onMessage;
    // 首次建连也可能晚于 REST 初始化；独立回调供页面补齐这段时间的推送空档。
    this.onOpen = onOpen;
    // 断线重连沿用原有回调，避免改变其他页面对重连事件的判断。
    this.onReconnect = onReconnect;
    this.emitGlobal = emitGlobal;
    this.connectionKey = `${param.func_name}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // 自动注册到全局管理器
    globalWebSocketManager.addConnection(this.connectionKey, this);
  }

  connect() {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }

    if (this.#reconnectAttempts >= this.#MAX_RECONNECT_ATTEMPTS) {
      // console.error(`${this.funcName}达到最大重连次数`);
      if (this.#pingIntervalId) clearInterval(this.#pingIntervalId);
      MessageBox.alert("由于长时间未操作或网络波动，请稍后刷新重试！", "提示", {
        type: "warning",
        confirmButtonText: "确定",
      }).then(() => window.location.reload());
      return;
    }

    this.#reconnectAttempts++;

    // console.log(`${this.funcName}: Attempting to establish websocket connection (Attempt ${this.#reconnectAttempts})`);

    this.socket = new WebSocket(this.url);
    this.socket.onopen = this.#onOpen;
    this.socket.onmessage = this.#onMessage;
    this.socket.onerror = this.#onError;
    this.socket.onclose = this.#onClose;
  }

  #onOpen = () => {
    const reconnected = this.#hasOpened;
    this.#hasOpened = true;
    // 真正的连接建立时只打印一次，避免服务端消息推送时刷屏。
    console.debug(`${this.funcName} socket 已建立连接`);
    // 仅当连接稳定保持一段时间后才重置重连计数，防止“连上即断”导致无限重连风暴
    // （每次重连服务端都可能重推一批消息，造成日志刷屏和主线程卡顿）。
    if (Date.now() - this.#lastOpenedAt > 10000) {
      this.#reconnectAttempts = 0;
    }
    this.#lastOpenedAt = Date.now();
    if (this.#pingIntervalId) clearInterval(this.#pingIntervalId); // 清理可能存在的心跳定时器
    this.#sendPing();
    if (reconnected) this.onReconnect?.();
    else this.onOpen?.();
  };

  #onError = (error) => {
    // console.error(`${this.funcName} websocket encountered an error:`, error);
    if (this.#shouldReconnect()) {
      this.#reconnectIfNeeded();
    }
  };

  #onClose = () => {
    // console.error(`${this.funcName} websocket onClosed`);
    clearInterval(this.#pingIntervalId);
    this.#pingIntervalId = null;
    if (this.#shouldReconnect()) {
      this.#reconnectIfNeeded();
    }
  };

  #shouldReconnect = () => {
    // 显式关闭优先，任何页面/登录态下都不再重连
    if (this.#manualShutDown) return false;
    if (localStorage.getItem("token")) return true;
    // 公共插码页无需登录，保持原有保活逻辑
    return location.pathname === "/chatBotInsertion";
  };

  #onMessage = (event) => {
    if (event.data) {
      let data = JSON.parse(event.data);
      if (data.code == 20001) {
        // Token 已过期，关闭连接统一交给 tokenExpired 里的 closeAllConnections 处理
        tokenExpired();
        router.push("/homepage");
      } else {
        if (this.emitGlobal) SocketEventCenter.emit(this.funcName, data);
        this.onMessage?.(data);
      }
    }
  };

  #sendPing = () => {
    const pingMessage = JSON.stringify(this.param);
    this.#pingIntervalId = setInterval(() => {
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        this.socket.send(pingMessage);
      }
    }, this.#PING_INTERVAL);
  };

  #reconnectIfNeeded() {
    if (this.socket && this.socket.readyState !== WebSocket.CLOSED) {
      return; // 如果当前连接未关闭，不进行重连
    }
    if (this.#reconnectAttempts < this.#MAX_RECONNECT_ATTEMPTS) {
      setTimeout(() => {
        // 页面切换或卸载可能发生在重连计时期间，显式关闭后不再创建旧会话连接。
        if (!this.#shouldReconnect()) return;
        // error 与 close 都可能排队重连；已有连接时只保留先执行的那一次。
        if (this.socket && this.socket.readyState !== WebSocket.CLOSED) return;
        this.connect();
      }, this.#RECONNECT_INTERVAL);
    }
  }

  send(message) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    } else {
      this.connect();
    }
  }

  close() {
    this.#manualShutDown = true;
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
    // 建连失败时 socket 也可能为空，仍需从全局管理器移除当前实例。
    globalWebSocketManager.removeConnection(this.connectionKey);
  }
}
